import * as compService from './component'
import {
  GLOBAL_LOGIN_COMPONENT_NAME,
  buildDefaultGlobalLoginComponent,
} from '../constants/globalLoginComponent'

/**
 * Resolve the published ComponentModel for the platform global login gate.
 */
export async function resolveGlobalLoginComponentModel() {
  const res = await compService.getGlobalComponents(0, 20, GLOBAL_LOGIN_COMPONENT_NAME)
  const versionMeta = (res.components || []).find(
    (c) => c.name === GLOBAL_LOGIN_COMPONENT_NAME,
  )
  if (!versionMeta) {
    return null
  }

  const version =
    versionMeta.published_version || versionMeta.current_version || '1.0'
  const models = await compService.getComponentByVersion(versionMeta._id, version)
  const component = Array.isArray(models) ? models[0] : models
  if (!component || !component._id || component.is_deleted) {
    return null
  }

  return {
    ...component,
    is_global: true,
  }
}

/**
 * Find or create the platform-wide global login component (`gg-global-login`).
 */
export async function ensureGlobalLoginComponent() {
  const existing = await resolveGlobalLoginComponentModel()
  if (existing) {
    return existing
  }

  const created = await compService.createComponent({
    component: buildDefaultGlobalLoginComponent(),
  })
  return {
    ...created,
    is_global: true,
  }
}

function findGlobalLoginIndex(components) {
  return components.findIndex(
    (c) => c.name === GLOBAL_LOGIN_COMPONENT_NAME && c.is_global,
  )
}

/** Detach only `gg-global-login` — never remove the site's custom `login` component. */
function removeGlobalLoginFromSite(components) {
  const loginIndex = findGlobalLoginIndex(components)
  if (loginIndex === -1) {
    return { removed: false }
  }

  const removedId = components[loginIndex]._id
  components.splice(loginIndex, 1)
  components.forEach((c) => {
    if (c.deps && c.deps.length) {
      c.deps = c.deps.filter((dep) => dep !== removedId)
    }
  })
  return { removed: true, removedGlobal: true }
}

/**
 * When Login Required (+ Include Login): always attach `gg-global-login`.
 * The site's own `login` component (if any) is left untouched.
 */
export async function syncSiteAuthLoginComponent({ site, components }) {
  const authLoginEnabled = !!site.loginRequired

  if (!authLoginEnabled) {
    return removeGlobalLoginFromSite(components)
  }

  const globalLogin = await ensureGlobalLoginComponent()
  const globalIndex = findGlobalLoginIndex(components)

  if (globalIndex === -1) {
    components.push({
      ...globalLogin,
      old_id: globalLogin._id,
      deps: globalLogin.deps || [],
    })
    return { created: true }
  }

  if (components[globalIndex]._id !== globalLogin._id) {
    const deps = components[globalIndex].deps || globalLogin.deps || []
    components[globalIndex] = {
      ...globalLogin,
      old_id: globalLogin._id,
      deps,
    }
    return { alreadyPresent: true }
  }

  return { alreadyPresent: true }
}
